import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { AtSign, Hash, Link2, Pencil, Plus, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@nodus/ui";
import {
  commsApi,
  type CommsTarget,
  type CommsTargetChannel,
  type CommsTargetScope,
} from "@/lib/api/comms";
import { tenantApi } from "@/lib/api/client";
import { defaultTargetCategories, targetCategoryOptions } from "@/lib/comms-targets";

/** Per-channel presentation + the copy that tells an admin what to type. */
const CHANNELS: {
  value: CommsTargetChannel;
  label: string;
  icon: typeof AtSign;
  addressLabel: string;
  placeholder: string;
  description: string;
}[] = [
  {
    value: "email",
    label: "Email",
    icon: AtSign,
    addressLabel: "Email address",
    placeholder: "reception@example.com",
    description: "A mailbox everyone here can read, e.g. a shared reception inbox.",
  },
  {
    value: "sms",
    label: "SMS",
    icon: Smartphone,
    addressLabel: "Phone number",
    placeholder: "+46701234567",
    description: "A phone number to text. Include the country code.",
  },
  {
    value: "slack_channel",
    label: "Slack channel",
    icon: Hash,
    addressLabel: "Channel",
    placeholder: "C01234567",
    description: "A channel in your connected Slack workspace.",
  },
  {
    value: "slack_webhook",
    label: "Slack webhook",
    icon: Link2,
    addressLabel: "Webhook URL",
    placeholder: "https://hooks.slack.com/services/...",
    description: "An incoming-webhook URL from Slack.",
  },
];

const SCOPE_COPY: Record<CommsTargetScope, { title: string; blurb: string; empty: string }> = {
  tenant: {
    title: "Shared with your team",
    blurb:
      "Destinations that everyone in your company shares — a reception inbox, an on-call phone, " +
      "a Slack channel. These fire in addition to each person's own settings above, so nobody " +
      "has to be signed in for the message to land somewhere.",
    empty: "Your team has no shared destinations yet.",
  },
  site: {
    title: "Shared across the building",
    blurb:
      "Destinations for the whole building — reception, the caretaker's phone, a building-wide " +
      "Slack channel. They receive notifications from every company on site, not just yours.",
    empty: "This building has no shared destinations yet.",
  },
};

function channelMeta(channel: string) {
  return CHANNELS.find((c) => c.value === channel) ?? CHANNELS[0];
}

/** Backend 400s carry a human-readable `detail` (bad email, non-Slack webhook
 *  URL, unknown category) — show that rather than a generic failure. */
function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || fallback;
}

interface FormState {
  channel: CommsTargetChannel;
  address: string;
  categories: string[];
  label: string;
}

const EMPTY_FORM: FormState = { channel: "email", address: "", categories: [], label: "" };

export interface TargetSectionProps {
  scope: CommsTargetScope;
}

/** Manage the notification destinations one scope (tenant or site) notifies.
 *  Rendered twice on the notification-settings page — once per scope the
 *  signed-in member administers. */
export function TargetSection({ scope }: TargetSectionProps) {
  const queryClient = useQueryClient();
  const queryKey = ["comms-targets", scope];
  const copy = SCOPE_COPY[scope];

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CommsTarget | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<CommsTarget | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const {
    data: targets = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: () => commsApi.getTargets(scope).then((r) => r.data),
  });

  // The category list is the same one the personal matrix above is built from.
  const { data: preferences = [] } = useQuery({
    queryKey: ["comms-preferences"],
    queryFn: () => commsApi.getPreferences().then((r) => r.data),
  });

  const categoryOptions = useMemo(() => targetCategoryOptions(preferences, scope), [preferences, scope]);
  const categoryLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of preferences) map[p.category] = p.label;
    return map;
  }, [preferences]);

  // Only fetched once someone actually picks a Slack channel target.
  const slackChannels = useQuery({
    queryKey: ["slack-channels"],
    queryFn: () => tenantApi.listSlackChannels().then((r) => r.data),
    enabled: modalOpen && form.channel === "slack_channel",
    staleTime: 60_000,
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (editing) {
        return commsApi.updateTarget(editing.id, {
          address: form.address.trim(),
          categories: form.categories,
          label: form.label.trim(),
        });
      }
      return commsApi.createTarget({
        scope,
        channel: form.channel,
        address: form.address.trim(),
        categories: form.categories,
        label: form.label.trim(),
      });
    },
    onSuccess: () => {
      toast.success(editing ? "Destination updated" : "Destination added");
      setModalOpen(false);
      invalidate();
    },
    onError: (err) => setFormError(errorDetail(err, "Couldn't save this destination.")),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ target, enabled }: { target: CommsTarget; enabled: boolean }) =>
      commsApi.updateTarget(target.id, { enabled }),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(errorDetail(err, "Couldn't change this destination.")),
    onSettled: () => setTogglingId(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (target: CommsTarget) => commsApi.deleteTarget(target.id),
    onSuccess: () => {
      toast.success("Destination removed");
      setDeleting(null);
      invalidate();
    },
    onError: (err) => toast.error(errorDetail(err, "Couldn't remove this destination.")),
  });

  const openAdd = () => {
    setEditing(null);
    setFormError(null);
    // Default to every category — a shared inbox that only gets half the
    // notifications is a surprise, and unticking is easier than hunting.
    setForm({ ...EMPTY_FORM, categories: defaultTargetCategories(preferences, scope) });
    setModalOpen(true);
  };

  const openEdit = (target: CommsTarget) => {
    setEditing(target);
    setFormError(null);
    setForm({
      channel: target.channel,
      address: target.address,
      categories: target.categories,
      label: target.label,
    });
    setModalOpen(true);
  };

  const handleSubmit = () => {
    setFormError(null);
    if (!form.address.trim()) {
      setFormError(`${channelMeta(form.channel).addressLabel} is required.`);
      return;
    }
    // Editing may clear every kind — the destination is kept but nothing is sent to it.
    if (!editing && form.categories.length === 0) {
      setFormError("Pick at least one kind of notification to send here.");
      return;
    }
    saveMutation.mutate();
  };

  const meta = channelMeta(form.channel);
  const slackOptions = (slackChannels.data ?? []).map((c) => ({
    value: c.id,
    label: `#${c.name}${c.is_member ? "" : " (bot not in channel)"}`,
  }));
  // Keep the saved channel selectable even when it no longer comes back from
  // Slack (bot removed, channel archived) — otherwise editing blanks it.
  if (form.address && !slackOptions.some((o) => o.value === form.address)) {
    slackOptions.unshift({ value: form.address, label: editing?.label || form.address });
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} maw={640}>
          <Text fw={600}>{copy.title}</Text>
          <Text fz="xs" c="dimmed">
            {copy.blurb}
          </Text>
        </Stack>
        <Button size="xs" leftSection={<Plus size={16} />} onClick={openAdd}>
          Add destination
        </Button>
      </Group>

      {isLoading ? (
        <Center py="lg">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Text fz="sm" c="red">
          Couldn't load these destinations. Please try again later.
        </Text>
      ) : targets.length === 0 ? (
        <Text fz="sm" c="dimmed">
          {copy.empty}
        </Text>
      ) : (
        <Stack gap="xs">
          {targets.map((target) => {
            const info = channelMeta(target.channel);
            const Icon = info.icon;
            return (
              <Card key={target.id} withBorder padding="sm">
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Icon size={18} style={{ color: "var(--mantine-color-dimmed)", flexShrink: 0 }} />
                    <Stack gap={4} style={{ minWidth: 0 }}>
                      <Text fz="sm" fw={500} style={{ wordBreak: "break-all" }}>
                        {target.label || target.address}
                      </Text>
                      {target.label && (
                        <Text fz="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                          {target.address}
                        </Text>
                      )}
                      <Group gap={4}>
                        {target.categories.map((category) => (
                          <Badge key={category} size="xs" variant="light">
                            {categoryLabels[category] ?? category}
                          </Badge>
                        ))}
                      </Group>
                    </Stack>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    <Tooltip label={target.enabled ? "Receiving notifications" : "Paused"} withArrow>
                      <Switch
                        checked={target.enabled}
                        disabled={togglingId === target.id}
                        onChange={(e) => {
                          setTogglingId(target.id);
                          toggleMutation.mutate({ target, enabled: e.currentTarget.checked });
                        }}
                        aria-label={`Send notifications to ${target.label || target.address}`}
                      />
                    </Tooltip>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => openEdit(target)}
                      aria-label="Edit destination"
                    >
                      <Pencil size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => setDeleting(target)}
                      aria-label="Remove destination"
                    >
                      <Trash2 size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit destination" : "Add destination"}
        centered
      >
        <Stack gap="md">
          <Select
            label="Send to"
            data={CHANNELS.map((c) => ({ value: c.value, label: c.label }))}
            value={form.channel}
            // The backend keys a target's identity on its channel — swap it by
            // removing this destination and adding a new one.
            disabled={!!editing}
            allowDeselect={false}
            description={editing ? undefined : meta.description}
            onChange={(value) =>
              setForm((f) => ({ ...f, channel: (value as CommsTargetChannel) ?? f.channel, address: "" }))
            }
          />

          {form.channel === "slack_channel" && !slackChannels.isError ? (
            <Select
              label={meta.addressLabel}
              placeholder={slackChannels.isLoading ? "Loading channels..." : "Select a channel"}
              data={slackOptions}
              value={form.address || null}
              disabled={slackChannels.isLoading}
              searchable
              allowDeselect={false}
              nothingFoundMessage="No channels — invite the Nodus bot to a channel first."
              onChange={(value) => setForm((f) => ({ ...f, address: value ?? "" }))}
            />
          ) : (
            <TextInput
              label={meta.addressLabel}
              placeholder={meta.placeholder}
              value={form.address}
              description={
                form.channel === "slack_channel"
                  ? "Slack channels couldn't be loaded — paste the channel ID instead."
                  : undefined
              }
              onChange={(e) => setForm((f) => ({ ...f, address: e.currentTarget.value }))}
            />
          )}

          <MultiSelect
            label="Notify about"
            placeholder={form.categories.length === 0 && !editing ? "Pick at least one" : undefined}
            description={
              editing && form.categories.length === 0
                ? "Nothing selected — this destination is kept but receives no notifications."
                : undefined
            }
            data={categoryOptions}
            value={form.categories}
            onChange={(value) => setForm((f) => ({ ...f, categories: value }))}
          />

          <TextInput
            label="Name"
            placeholder="Reception desk"
            description="Optional — helps everyone recognise this destination."
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.currentTarget.value }))}
          />

          {formError && (
            <Alert color="red" variant="light">
              {formError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={saveMutation.isPending}>
              {editing ? "Save" : "Add destination"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={!!deleting}
        onClose={() => setDeleting(null)}
        title="Remove destination"
        description={
          deleting
            ? `${deleting.label || deleting.address} will stop receiving these notifications.`
            : ""
        }
        hardDeleteLabel="Remove"
        isSubmitting={deleteMutation.isPending}
        onHardDelete={() => deleting && deleteMutation.mutate(deleting)}
      />
    </Stack>
  );
}
